
/**
 * Asks the server whether THIS visitor needs the consent banner.
 *
 * The banner applies to California and Florida. Everyone else has consent
 * auto-granted and never sees it.
 *
 * ## Why this is a server call and not a client guess
 *
 * The two client-only options were a third-party geo script — exactly the kind
 * of third party we refuse to load before consent — or the browser timezone.
 * Timezone was tried and dropped: CA and FL have no dedicated IANA zone, so
 * covering them means gating all of Pacific/Eastern/Central, and it fails in
 * the WRONG direction because a Californian on a VPN or with a skewed clock
 * reads as ungated and gets tracked.
 *
 * It can be asynchronous only because Consent Mode v2 defaults=denied already
 * ships: the page starts fully withheld with no Pixel injected, so nothing
 * leaks while this resolves. That is the change that made a correct answer
 * affordable.
 *
 * ## Where the request actually comes from
 *
 * Usually not from here. `public/index.html` issues it from `<head>` and
 * publishes the in-flight promise on `window.__theaConsent.geo`; this module
 * adopts that promise instead of starting a second request. The bundle is the
 * fallback leg, not the default one.
 *
 * That inversion is the fix for THE-524. Asking from the bundle meant the
 * request could not be SENT until React had booted and the Firebase chunks had
 * loaded — 2735 ms of a 3196 ms budget on fiber, all of it before the ~400 ms
 * server call. The deadline was spent on everything except the question. From
 * `<head>` the request races the bundle download rather than queueing behind
 * it, and the answer is typically already waiting when the app asks.
 *
 * ## Fail-safe
 *
 * Any failure — network, timeout, rate limit, an unparseable response, no
 * dataset on the server — resolves to "consent required". The server fails
 * safe too, so this is defence in depth rather than the only guard.
 *
 * What changed with THE-524 is how OFTEN that path is taken, not what it
 * means. It was firing for every visitor on a slow connection, which made the
 * fail-safe the normal outcome on mobile rather than the exceptional one. A
 * fail-safe that fires routinely is not a safety net, it is the behaviour.
 * `source: 'fail-safe'` exists so that rate is now visible — see
 * `ConsentBannerHost`, which reports it.
 */

import { readBootstrap } from './consentTags';

/**
 * Per-session cache key. Session, NOT local, on purpose — see below.
 *
 * Exported because `public/index.html` hand-mirrors it in ES5 and
 * `consentBootstrapParity.test.ts` asserts the two match. Drift would make
 * the bootstrap's answer invisible to this module, which silently restores
 * the pre-THE-524 behaviour: a second request, from the bundle, inside the
 * deadline, exactly as before.
 */
export const GEO_SESSION_KEY = 'thea.consentGeo';

/**
 * Hard ceiling on how long a visitor waits before the banner appears.
 *
 * The callable carries its own timeout, but that does not cover every way a
 * request can hang (DNS, a captive portal, a suspended tab resuming). This
 * race guarantees the banner is never withheld indefinitely by a stuck call —
 * the failure mode would otherwise be a visitor who is never asked AND never
 * tracked, which looks like nothing is wrong.
 *
 * ## Why this is 15 s and not 4 s (THE-524)
 *
 * It was 4000 ms, sized against a chain that took ~2.7–3.2 s on fiber — about
 * a second of headroom. Then the bundle grew (836 KB over 34 commits) and ate
 * it. On 4G the same chain measured 7546 ms and on 3G it had not answered
 * after 16 s, so the deadline fired first and every one of those visitors got
 * `FAIL_SAFE`, i.e. the banner, regardless of what state they were in. The
 * server had answered correctly; nobody was listening (THE-521).
 *
 * A deadline on this call buys NO safety, and that is the point people get
 * backwards. Tags are withheld for the entire time it is in flight — Consent
 * Mode defaults are denied and fbevents.js is not in the page — so waiting
 * longer cannot leak anything. All the deadline does is convert "slow" into
 * "show a disclosure to someone who should not get one", which is wrong on
 * both axes: it misinforms that visitor AND it costs pixel signal on the
 * ~80% of traffic this gate exists to keep firing.
 *
 * What it must still do is bound a call that will never answer at all, so the
 * banner cannot be withheld forever from someone who does need it. 15 s clears
 * the measured 3G distribution with room, and the visitor is looking at a
 * loading page for much of it. Raise it before you lower it.
 */
const GEO_TIMEOUT_MS = 15_000;

/** Where an outcome came from. Telemetry only — never a policy input. */
export type GeoSource =
  /** The per-session cache: this tab already asked. */
  | 'cache'
  /** The request `public/index.html` started before the bundle loaded. */
  | 'bootstrap'
  /** The in-bundle callable, used when the bootstrap had no answer. */
  | 'sdk'
  /** No answer from anywhere; `FAIL_SAFE`. The rate to watch. */
  | 'fail-safe';

export interface GeoOutcome {
  /** True when this visitor must be asked. Every failure resolves to true. */
  consentRequired: boolean;
  /** False when we fell back rather than got a real answer. Telemetry only. */
  resolved: boolean;
  /** Which leg produced this. Telemetry only. */
  source: GeoSource;
}

const FAIL_SAFE: GeoOutcome = {
  consentRequired: true,
  resolved: false,
  source: 'fail-safe',
};

/**
 * Read the per-session cache.
 *
 * sessionStorage rather than localStorage is deliberate. A geo answer is a
 * fact about where someone is right now, not a decision they made: caching it
 * durably would mean a visitor who travels into California keeps the stale
 * "no banner needed" answer indefinitely. One call per session per tab is the
 * right trade, and it is what the brief asked for.
 *
 * Only SUCCESSFUL resolutions are cached. A cached failure would pin the
 * banner for the whole session over one blip.
 */
function readCache(): GeoOutcome | null {
  try {
    const raw = window.sessionStorage.getItem(GEO_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GeoOutcome>;
    if (typeof parsed.consentRequired !== 'boolean') return null;
    // `resolved: true` is sound only because `writeCache` refuses to store an
    // unresolved outcome. The serialised form carries no `resolved` field, so
    // these two functions are one contract: relax the writer and this line
    // silently starts reporting fail-safes as real answers.
    return { consentRequired: parsed.consentRequired, resolved: true, source: 'cache' };
  } catch {
    return null; // Private mode / storage disabled — just call again.
  }
}

/**
 * Persist a geo answer for the rest of the session.
 *
 * Refuses anything with `resolved: false`. That value is not "an answer we are
 * unsure about", it is the server telling us it could not place the address at
 * all (`uncovered`, `no_v6_table`, `no_dataset`, throttled) — or our own
 * fail-safe. Caching it costs twice over: it pins the banner for the whole
 * session over one blip, and since the cache cannot carry `resolved`, every
 * later read in that session reports the fail-safe as a real answer. That is
 * precisely the quantity `consent_banner_impression.geo_resolved` exists to
 * measure, so caching an unresolved answer biases the metric toward "healthy"
 * in exactly the scenario it was added to catch. Dropping it instead means we
 * simply ask again on the next pageview, which may well succeed.
 */
function writeCache(outcome: GeoOutcome): void {
  if (!outcome.resolved) return;
  try {
    window.sessionStorage.setItem(
      GEO_SESSION_KEY,
      JSON.stringify({ consentRequired: outcome.consentRequired }),
    );
  } catch {
    // Storage unavailable. We simply ask again next page — harmless.
  }
}

/** Test-only: drop the session cache. */
export function __clearGeoCacheForTests(): void {
  try {
    window.sessionStorage.removeItem(GEO_SESSION_KEY);
  } catch {
    /* no-op */
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(null);
      }
    }, ms);
    promise.then(
      (value) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(value);
        }
      },
      () => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(null);
        }
      },
    );
  });
}

/**
 * The bootstrap's in-flight request, if `public/index.html` started one.
 *
 * Resolves to `null` when it had no usable answer — the bootstrap starts the
 * request but never decides what a miss means. That decision is made here,
 * because only this module has a second leg to try.
 */
async function bootstrapLeg(budgetMs: number): Promise<GeoOutcome | null> {
  const pending = readBootstrap()?.geo;
  if (!pending) return null;

  const settled = await withTimeout(pending, budgetMs);
  if (!settled || typeof settled.consentRequired !== 'boolean') return null;

  return {
    consentRequired: settled.consentRequired,
    resolved: settled.resolved !== false,
    source: 'bootstrap',
  };
}

/**
 * Ask the callable from inside the bundle. The original path, now the
 * FALLBACK rather than the default.
 *
 * Still needed, and not only for old cached HTML: `public/index.html` skips
 * the request when the browser has no `fetch`, and there is no bootstrap at
 * all under jsdom or a prerender host. This leg also still works when the
 * Hosting rewrite does not (local `npm start`, the emulator), which is what
 * keeps development honest about the rest of the flow.
 */
async function sdkLeg(budgetMs: number): Promise<GeoOutcome | null> {
  // Both imports are DYNAMIC. `callables` reaches `firebaseFunctions`, which
  // calls `getFunctions(getAppInstance())` at module scope — a static import
  // would drag the whole Firebase Functions SDK into the module graph of
  // anything that touches consent, which broke 19 unrelated test suites and
  // would pull the SDK into whatever chunk the banner lands in. Deferring it
  // to the one moment we actually call the server costs nothing: this is
  // already an async path behind a timeout.
  //
  // ensureAuth() is NO LONGER awaited here. `resolveConsentGeo` used to
  // require a signed-in caller, so this path had to wait out an anonymous
  // `accounts:signUp` round trip — ~660 ms on fiber and the single largest
  // avoidable item inside the old deadline. The op now takes unauthenticated
  // callers (THE-524; it has to, or the bootstrap could not ask at all), and
  // the SDK attaches whatever token already exists, so a returning visitor
  // still gets the per-uid rate-limit key at no latency cost.
  const response = await withTimeout(
    (async () => {
      const { resolveConsentGeo } = await import('../callables');
      return resolveConsentGeo({});
    })(),
    budgetMs,
  );

  const data = response?.data;
  if (!data || typeof data.consentRequired !== 'boolean') return null;

  return {
    consentRequired: data.consentRequired,
    resolved: data.resolved !== false,
    source: 'sdk',
  };
}

/**
 * Resolve whether this visitor needs the banner. Never rejects.
 *
 * Cached per session, so a normal visitor spends exactly one call regardless
 * of how many pages they view.
 *
 * ## The order, and why
 *
 * cache → bootstrap → callable → fail-safe. The two network legs share ONE
 * budget measured from entry, so the fallback cannot silently double the wait:
 * a bootstrap request that hangs for the whole deadline leaves the SDK leg
 * nothing, which is correct — the visitor has already waited the maximum we
 * are willing to make them wait.
 */
export async function fetchConsentGeo(): Promise<GeoOutcome> {
  if (typeof window === 'undefined') return FAIL_SAFE;

  const cached = readCache();
  if (cached) return cached;

  const deadline = Date.now() + GEO_TIMEOUT_MS;
  const remaining = (): number => Math.max(0, deadline - Date.now());

  const outcome = (await bootstrapLeg(remaining())) ?? (await sdkLeg(remaining()));

  // Timed out on both legs, threw, or answered in a shape we don't recognise.
  if (!outcome) return FAIL_SAFE;

  writeCache(outcome);
  return outcome;
}
